import { AbstractControl, ValidationErrors, Validator, ValidatorFn } from '@angular/forms';
import { PhoneNumberUtil } from 'google-libphonenumber';

const phoneUtil = PhoneNumberUtil.getInstance();

const COUNTRY_CODE_ONLY = 'country-code-only';

function validatePhoneNumber(trimmed: string): ValidationErrors | null | typeof COUNTRY_CODE_ONLY {
  try {
    const number = phoneUtil.parse(trimmed);
    const nationalNumber = phoneUtil.getNationalSignificantNumber(number);

    if (!nationalNumber || nationalNumber === '0') {
      return COUNTRY_CODE_ONLY;
    }

    return phoneUtil.isValidNumber(number) ? null : { invalidPhoneNumber: { value: trimmed } };
  } catch {
    const withoutPlus = trimmed.startsWith('+') ? trimmed.substring(1) : trimmed;
    const countryCode = parseInt(withoutPlus, 10);
    const regionCode = phoneUtil.getRegionCodeForCountryCode(countryCode);

    // Only treat as country code if it's a valid region (not 'ZZ' for unknown)
    if (!isNaN(countryCode) && regionCode && regionCode !== 'ZZ') {
      return COUNTRY_CODE_ONLY;
    }

    return { invalidPhoneNumber: { value: trimmed } };
  }
}

/**
 * Phone input validator that only validates format, ignoring empty values and country-code-only inputs.
 * This validator is intended to be used as a built-in validator for phone input components.
 */
export class PhoneInputValidator implements Validator {
  validate(control: AbstractControl<string>): ValidationErrors | null {
    const trimmed = control.value?.trim();
    if (!trimmed) return null;

    const result = validatePhoneNumber(trimmed);

    // For format validator: only return error if it's definitely invalid format
    // Don't mark country-code-only as invalid (let required validators handle that)
    return result === COUNTRY_CODE_ONLY ? null : result;
  }
}

/**
 * Phone input required validator that only validates presence (not format).
 * This validator should be used when phone number is a required field.
 * Format validation is handled by the PhoneInputValidator.
 */
export class PhoneInputRequiredValidator implements Validator {
  validate(control: AbstractControl<string>): ValidationErrors | null {
    const trimmed = control.value?.trim();
    if (!trimmed) return { invalidPhoneNumber: { value: trimmed || '' } };

    const result = validatePhoneNumber(trimmed);

    // For required validator: only check if it's country-code-only
    // Format validation is handled by the built-in PhoneInputValidator
    return result === COUNTRY_CODE_ONLY ? { invalidPhoneNumber: { value: trimmed } } : null;
  }
}

export function phoneInputRequiredValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    return new PhoneInputRequiredValidator().validate(control);
  };
}
